import React, { useState } from "react";
import {
  useLocation,
  useNavigate
} from "react-router-dom";

import {
  Header,
  Label,
  Button,
  Icon,
  Popup,
  Loader,
  Table,
  Segment,
  Grid,
  Pagination,
} from "semantic-ui-react";

import { useGetFeedbackSessionsQuery } from "./FeedbackApiSlice";
import FeedbackLog from "./FeedbackLog"
import ChatSessionDetail from "../chat_sessions/ChatSessionDetail";
import { PAGE_SIZE, formatDate } from "../../constants";

const FeedbackSessionsList = () => {
  // Get query params
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const page = queryParams.get("page");
  const page_size = queryParams.get("page_size");

  const author = queryParams.get("author");
  const persona = queryParams.get("persona");
  const partner = queryParams.get("partner");

  // Remove null ones to pass to backend
  let qp = { page, page_size, author, persona, partner };
  qp = Object.entries(qp)
    .filter((e) => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  // GET feedback sessions
  const { data, isLoading, isSuccess, isError, error } = useGetFeedbackSessionsQuery({ ...qp });

  // Declare constants to handle session selection
  const [selectedFeedbackSession, setSelectedFeedbackSession] = useState(null);
  const handleSelectFeedbackSession = (feedback_session) => {
    setSelectedFeedbackSession(feedback_session);
  };

  // Declare constants to handle change of pagination
  const navigate = useNavigate();
  const handlePaginationChange = (e, { activePage }) => {
    setSelectedFeedbackSession(null);
    let url = `/feedbackslist?page=${activePage}&page_size=${PAGE_SIZE}`;
    if (author) {
      url = url + `&author=${author}`;
    }
    if (persona) {
      url = url + `&persona=${persona}`;
    }
    if (partner) {
      url = url + `&partner=${partner}`;
    }
    navigate(url);
  };

  // Page content
  let content;
  if (isLoading) {
    content = <Loader active />;
  } else if (isSuccess) {
    content = (
      <>
      <Grid columns={3}>

      <Grid.Row>
        <Grid.Column width={10}>
          <Header as="h1"><Icon name="edit outline"/>Feedback Sessions</Header>
        </Grid.Column>
        <Grid.Column width={3}>
          <Header as="h1">Original Chats</Header>
        </Grid.Column>
        <Grid.Column width={3}>
          <Header as="h1">Feedback Logs</Header>
        </Grid.Column>
      </Grid.Row>

      <Grid.Column width={10}>
        {isLoading && <Loader active />}
        <Pagination
          attached="top"
          ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
          firstItem={{ content: <Icon name="angle double left" />, icon: true }}
          lastItem={{ content: <Icon name="angle double right" />, icon: true }}
          prevItem={{ content: <Icon name="angle left" />, icon: true }}
          nextItem={{ content: <Icon name="angle right" />, icon: true }}
          activePage={page}
          onPageChange={handlePaginationChange}
          totalPages={Math.ceil(data.count / page_size)}
        />

        <Table compact="very" celled attached>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>ID</Table.HeaderCell>
              <Table.HeaderCell>Created At</Table.HeaderCell>

              <Table.HeaderCell><Icon name="chat" color="grey" />Persona</Table.HeaderCell>
              <Table.HeaderCell><Icon name="chat" color="blue" />Author</Table.HeaderCell>
              <Table.HeaderCell>Language</Table.HeaderCell>

              <Table.HeaderCell>Edit</Table.HeaderCell>
              <Table.HeaderCell>Evaluations</Table.HeaderCell>
              <Table.HeaderCell>Detail</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {data.results.map((feedback_session, i) => (
              <Table.Row
                key={feedback_session.id}
                positive={
                  selectedFeedbackSession &&
                  selectedFeedbackSession.id === feedback_session.id
                }
              >
                <Table.Cell textAlign="center">
                  <Label circular>
                    <Icon name="comments alternate outline" />
                    {feedback_session.id.slice(0, 5)}
                  </Label>
                </Table.Cell>

                <Table.Cell>
                  {formatDate(feedback_session.created_at)}
                </Table.Cell>

                <Table.Cell>
                  <Popup
                    header={feedback_session.persona? feedback_session.persona.name : ""}
                    content={feedback_session.persona? feedback_session.persona.purpose_of_visit : ""}
                    trigger={<span>{feedback_session.persona? feedback_session.persona.name: ""}</span>}
                  />
                </Table.Cell>

                <Table.Cell>
                  {feedback_session.author? feedback_session.author.username : ""}
                </Table.Cell>

                <Table.Cell>
                  {feedback_session.language}
                </Table.Cell>

                <Table.Cell>
                  <Button fluid size="mini" icon="edit"
                      onClick={() => {
                        navigate(`/feedbackperform?feedback_session_id=${feedback_session.id}&chat_session_id=${feedback_session.original_session.id}&edit=true`);
                      }}
                  />
                </Table.Cell>

                <Table.Cell>
                  <Button fluid size="mini" icon="external alternate"
                    onClick={() => {
                      navigate(`/evaluationsessionslist?page=&page_size=${PAGE_SIZE}&chat_session=${feedback_session.original_session.id}`);
                    }}
                  >
                    {feedback_session.evaluation_session_count}
                    <Icon style={{ paddingLeft: 10 }} name="external" />
                  </Button>
                </Table.Cell>

                <Table.Cell>
                  <Button fluid size="mini" primary inverted icon="arrow right"
                      active={
                        selectedFeedbackSession &&
                        selectedFeedbackSession.id === feedback_session.id
                      }
                      onClick={() => {
                        handleSelectFeedbackSession(feedback_session);
                      }}
                    >
                      <Icon name="arrow right" />
                    </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <Pagination
          attached="bottom"
          ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
          firstItem={{ content: <Icon name="angle double left" />, icon: true }}
          lastItem={{ content: <Icon name="angle double right" />, icon: true }}
          prevItem={{ content: <Icon name="angle left" />, icon: true }}
          nextItem={{ content: <Icon name="angle right" />, icon: true }}
          activePage={page}
          onPageChange={handlePaginationChange}
          totalPages={Math.ceil(data.count / page_size)}
        />
      </Grid.Column>

      <Grid.Column width={3}>
        {selectedFeedbackSession ? (
          <Segment raised placeholder={selectedFeedbackSession.chatlog_count < 1} className="scrollable-right-pane">
             {selectedFeedbackSession.chatlog_count > 0 ? (
              <ChatSessionDetail
                  key={selectedFeedbackSession.original_session.id}
                  chat_session_id={selectedFeedbackSession.original_session.id}
                />
              ) : (
                <Header icon>
                  There are no chat logs in this session.
                </Header>
            )}
          </Segment>
          ) : (
          <Segment raised placeholder className="scrollable-right-pane">
            <Header icon>
              Click the button under "Detail" from the left to see the original chats.
            </Header>
          </Segment>
        )}
      </Grid.Column>

      <Grid.Column width={3}>
        {selectedFeedbackSession ? (
          <Segment raised placeholder={selectedFeedbackSession.feedbacklog_count < 1} className="scrollable-right-pane">
            {selectedFeedbackSession.feedbacklog_count > 0 ? (
                <FeedbackLog
                  key={selectedFeedbackSession.id}
                  feedback_session_id={selectedFeedbackSession.id}
                />
              ) : (
                <Header icon>
                  There are no feedbacks in this session.
                </Header>
            )}
          </Segment>
          ) : (
          <Segment raised placeholder className="scrollable-right-pane">
            <Header icon>
              Click the button under "Detail" from the left to see the feedbacks.
            </Header>
          </Segment>
        )}
      </Grid.Column>
      </Grid>
      </>
    );
  } else if (isError) {
    content = null;
  }

  return content;
};
export default FeedbackSessionsList;